/**
 * @fileoverview API error message extraction
 * @description Pulls the human-readable error message out of an Axios error.
 * The axios response interceptor rejects the raw AxiosError, so the real
 * backend message lives at `error.response.data.*`, not on `error.message`
 * (which is only the generic "Request failed with status code 4xx").
 * @module utils/apiError
 */

/**
 * Extract the most meaningful error message from an unknown thrown value.
 *
 * Backend shapes handled, in priority order:
 *  1. { detail: "message" }                              -> string detail
 *  2. { detail: { Error: "...", StatusCode: 400 } }      -> dict detail (most common)
 *  3. { success: false, message: "...", error: "..." }   -> 409 conflict envelope
 *  4. { Error: "..." } / { error: "..." }                -> top-level field
 *  5. axios err.message ("Request failed with status code 400")
 *  6. fallback
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { response?: { data?: unknown }; message?: string };
    const data = e.response?.data;

    if (data && typeof data === 'object') {
      const d = data as {
        detail?: unknown;
        message?: unknown;
        Error?: unknown;
        error?: unknown;
      };

      // 1 & 2: detail is either a string or an object { Error, message }
      if (typeof d.detail === 'string' && d.detail.trim()) return d.detail;
      if (d.detail && typeof d.detail === 'object') {
        const det = d.detail as { Error?: unknown; message?: unknown };
        if (typeof det.Error === 'string' && det.Error.trim()) return det.Error;
        if (typeof det.message === 'string' && det.message.trim())
          return det.message;
      }

      // 3: 409 conflict envelope / generic message field
      if (typeof d.message === 'string' && d.message.trim()) return d.message;

      // 4: top-level Error / error
      if (typeof d.Error === 'string' && d.Error.trim()) return d.Error;
      if (typeof d.error === 'string' && d.error.trim()) return d.error;
    } else if (typeof data === 'string' && data.trim()) {
      // some endpoints return a plain string body
      return data;
    }

    // 5: generic axios message
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }

  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
